import type { components } from "@majitap/sdk";

type Schemas = components["schemas"];
export type Tone = "neutral" | "success" | "info" | "warning" | "danger";
export type OrderStatus = Schemas["AdminOrderSummary"]["status"];
export type PaymentStatus = Schemas["AdminOrderSummary"]["payment_status"];
export type DeliveryStatus = Schemas["AdminOrder"]["deliveries"][number]["status"];

/** What staff read for each status, and its badge colour. The database sets these; the console only shows them. */
export const ORDER_STATUS: Record<OrderStatus, [label: string, tone: Tone]> = {
  pending: ["Pending", "neutral"],
  accepted: ["Accepted", "info"],
  processing: ["Processing", "info"],
  partially_delivered: ["Partially delivered", "info"],
  delivered: ["Delivered", "success"],
  cancelled: ["Cancelled", "neutral"],
  suspended: ["Suspended", "warning"],
  failed: ["Failed", "danger"],
};

export type AcquisitionSource = NonNullable<Schemas["Customer"]["acquisition_source"]>;

/** How an account was won (P15-14). */
export const ACQUISITION_SOURCE: Record<AcquisitionSource, string> = {
  agent: "A sales agent",
  referral: "A referral",
  website: "The website",
  app: "The customer app",
  api: "An integration",
  walk_in: "Walked in",
  phone: "A phone call",
  migrated: "Moved from the old system",
  other: "Other",
};

/** A credit term (net days, end of month): billed on account, invoiced later, so unpaid isn't "to collect now". */
export const isCreditTerm = (kind: string | null | undefined) => kind === "net_days" || kind === "end_of_month";

export const PAYMENT_STATUS: Record<PaymentStatus, [label: string, tone: Tone]> = {
  unpaid: ["Unpaid", "warning"],
  pending: ["Payment pending", "info"],
  partial: ["Part paid", "warning"],
  paid: ["Paid", "success"],
  failed: ["Payment failed", "danger"],
  refunded: ["Refunded", "neutral"],
  disputed: ["Disputed", "danger"],
};

export const DELIVERY_STATUS: Record<DeliveryStatus, [label: string, tone: Tone]> = {
  pending: ["Pending", "neutral"],
  fillsite_assigned: ["Fill site assigned", "info"],
  assigned: ["Truck assigned", "info"],
  accepted: ["Accepted by driver", "info"],
  at_fillsite: ["At the fill site", "info"],
  in_transit: ["On the way", "info"],
  at_customer: ["At the customer", "info"],
  delivered: ["Delivered", "success"],
  failed: ["Failed", "danger"],
  cancelled: ["Cancelled", "neutral"],
  rerouted: ["Rerouted", "warning"],
  suspended: ["Suspended", "warning"],
};

export const CHANNEL: Record<Schemas["AdminOrderSummary"]["channel"], string> = {
  admin: "Console (staff)",
  customer_app: "Customer app",
  customer_portal: "Customer portal",
  api: "API",
};

export type ApprovalStatus = Schemas["Partner"]["approval_status"];

/** Partners, drivers and fill sites: whether staff have approved them. */
export const APPROVAL_STATUS: Record<ApprovalStatus, [label: string, tone: Tone]> = {
  pending: ["Pending", "warning"],
  approved: ["Approved", "success"],
  rejected: ["Rejected", "danger"],
  suspended: ["Suspended", "danger"],
};
